import { alpha, createTheme, type PaletteMode } from '@mui/material/styles';

/**
 * TailAdmin design system. Raw scales are used where a real colour value is needed
 * (charts, alpha()); everything else goes through the `tokens` CSS variables below so
 * light and dark mode switch without re-rendering.
 */
export const brand = {
  25: '#f2f7ff', 50: '#ecf3ff', 100: '#dde9ff', 200: '#c2d6ff', 300: '#9cb9ff', 400: '#7592ff',
  500: '#465fff', 600: '#3641f5', 700: '#2a31d8', 800: '#252dae', 900: '#262e89', 950: '#161950',
} as const;
export const gray = {
  25: '#fcfcfd', 50: '#f9fafb', 100: '#f2f4f7', 200: '#e4e7ec', 300: '#d0d5dd', 400: '#98a2b3',
  500: '#667085', 600: '#475467', 700: '#344054', 800: '#1d2939', 900: '#101828', 950: '#0c111d', dark: '#1a2231',
} as const;
export const success = { 50: '#ecfdf3', 100: '#d1fadf', 500: '#12b76a', 600: '#039855', 700: '#027a48' } as const;
export const error = { 50: '#fef3f2', 100: '#fee4e2', 300: '#fda29b', 500: '#f04438', 600: '#d92d20', 700: '#b42318' } as const;
export const warning = { 50: '#fffaeb', 400: '#fdb022', 500: '#f79009', 600: '#dc6803', 700: '#b54708' } as const;
export const orange = { 50: '#fff6ed', 400: '#fd853a', 500: '#fb6514', 600: '#ec4a0a' } as const;
export const blueLight = { 50: '#f0f9ff', 500: '#0ba5ec', 600: '#0086c9' } as const;

export const shadows = {
  xs: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
  sm: '0px 1px 3px 0px rgba(16, 24, 40, 0.1), 0px 1px 2px 0px rgba(16, 24, 40, 0.06)',
  md: '0px 4px 8px -2px rgba(16, 24, 40, 0.1), 0px 2px 4px -2px rgba(16, 24, 40, 0.06)',
  lg: '0px 12px 16px -4px rgba(16, 24, 40, 0.08), 0px 4px 6px -2px rgba(16, 24, 40, 0.03)',
  xl: '0px 20px 24px -4px rgba(16, 24, 40, 0.08), 0px 8px 8px -4px rgba(16, 24, 40, 0.03)',
} as const;

export const ease = 'cubic-bezier(0.4, 0, 0.2, 1)';

// [name, light, dark]
const VARS: Array<[string, string, string]> = [
  ['ink', gray[800], 'rgba(255,255,255,0.9)'],
  ['ink2', gray[700], gray[300]],
  ['muted', gray[500], gray[400]],
  ['subtle', gray[400], gray[500]],
  ['line', gray[200], gray[800]],
  ['line-soft', gray[100], gray[800]],
  ['input-line', gray[300], gray[700]],
  ['surface', '#ffffff', gray[900]],
  ['card', '#ffffff', 'rgba(255,255,255,0.03)'],
  ['popover', '#ffffff', gray.dark],
  ['canvas', gray[50], gray[900]],
  ['wash', gray[50], 'rgba(255,255,255,0.03)'],
  ['hover', gray[100], 'rgba(255,255,255,0.05)'],
  ['icon-box', gray[100], gray[800]],
  ['icon-ink', gray[800], 'rgba(255,255,255,0.9)'],
  ['brand', brand[500], brand[500]],
  ['brand-text', brand[500], brand[400]],
  ['brand-soft', brand[50], 'rgba(70,95,255,0.12)'],
  ['success-soft', success[50], 'rgba(18,183,106,0.15)'],
  ['success-text', success[600], success[500]],
  ['error-soft', error[50], 'rgba(240,68,56,0.15)'],
  ['error-text', error[600], error[500]],
  ['warning-soft', warning[50], 'rgba(247,144,9,0.15)'],
  ['warning-text', warning[600], orange[400]],
  ['orange-soft', orange[50], 'rgba(251,101,20,0.15)'],
  ['orange-text', orange[600], orange[400]],
  ['info-soft', blueLight[50], 'rgba(11,165,236,0.15)'],
  ['info-text', blueLight[600], blueLight[500]],
  ['neutral-soft', gray[100], 'rgba(255,255,255,0.05)'],
  ['neutral-text', gray[700], 'rgba(255,255,255,0.8)'],
  ['scroll-thumb', gray[200], gray[700]],
];

const v = (name: string) => `var(--ta-${name})`;

/** Mode-aware design tokens (CSS variables) for use in `sx`. */
export const tokens = {
  ink: v('ink'),
  ink2: v('ink2'),
  muted: v('muted'),
  subtle: v('subtle'),
  line: v('line'),
  lineSoft: v('line-soft'),
  inputLine: v('input-line'),
  surface: v('surface'),
  card: v('card'),
  popover: v('popover'),
  canvas: v('canvas'),
  wash: v('wash'),
  hover: v('hover'),
  iconBox: v('icon-box'),
  iconInk: v('icon-ink'),
  brand: v('brand'),
  brandText: v('brand-text'),
  brandSoft: v('brand-soft'),
  successSoft: v('success-soft'),
  successText: v('success-text'),
  errorSoft: v('error-soft'),
  errorText: v('error-text'),
  warningSoft: v('warning-soft'),
  warningText: v('warning-text'),
  orangeSoft: v('orange-soft'),
  orangeText: v('orange-text'),
  infoSoft: v('info-soft'),
  infoText: v('info-text'),
  neutralSoft: v('neutral-soft'),
  neutralText: v('neutral-text'),
  shadowXs: shadows.xs,
  shadowMd: shadows.md,
  shadowLg: shadows.lg,
} as const;

/** Global CSS: the token variables for both modes. Light is the default; `data-theme="dark"` on <html> switches. */
export const cssVariables = {
  ':root': Object.fromEntries(VARS.map(([n, l]) => [`--ta-${n}`, l])),
  ':root[data-theme="dark"]': Object.fromEntries(VARS.map(([n, , d]) => [`--ta-${n}`, d])),
};

/** Real colour values for charts (SVG attributes can't rely on CSS variables everywhere). */
export function chartColors(mode: PaletteMode) {
  const dark = mode === 'dark';
  return {
    series1: brand[500],
    series2: brand[300],
    grid: dark ? gray[800] : gray[100],
    axis: dark ? gray[400] : gray[500],
    label: dark ? gray[300] : gray[700],
    cursor: dark ? 'rgba(255,255,255,0.04)' : 'rgba(16,24,40,0.04)',
    surface: dark ? gray[900] : '#ffffff',
  };
}

const font = '"Outfit Variable", "Outfit", "Segoe UI", system-ui, -apple-system, Roboto, Helvetica, Arial, sans-serif';

export function createAppTheme(mode: PaletteMode) {
  const dark = mode === 'dark';
  const c = {
    ink: dark ? 'rgba(255,255,255,0.9)' : gray[800],
    ink2: dark ? gray[300] : gray[700],
    muted: dark ? gray[400] : gray[500],
    line: dark ? gray[800] : gray[200],
    lineSoft: dark ? gray[800] : gray[100],
    inputLine: dark ? gray[700] : gray[300],
    paper: dark ? gray.dark : '#ffffff',
    hover: dark ? 'rgba(255,255,255,0.05)' : gray[100],
  };

  return createTheme({
    palette: {
      mode,
      primary: { main: brand[500], dark: brand[600], light: brand[400], contrastText: '#fff' },
      secondary: { main: gray[700] },
      success: { main: success[500], dark: success[600], light: success[50] },
      warning: { main: warning[500], dark: warning[600], light: warning[50] },
      error: { main: error[500], dark: error[600], light: error[50] },
      info: { main: blueLight[500] },
      text: { primary: c.ink, secondary: c.muted, disabled: dark ? gray[600] : gray[400] },
      divider: c.line,
      background: { default: dark ? gray[900] : gray[50], paper: c.paper },
      action: { hover: c.hover, selected: alpha(brand[500], dark ? 0.12 : 0.08) },
    },
    shape: { borderRadius: 8 },
    typography: {
      fontFamily: font,
      fontSize: 14,
      h4: { fontWeight: 600, fontSize: '1.375rem', lineHeight: 1.4 },
      h5: { fontWeight: 600, fontSize: '1.25rem' },
      h6: { fontWeight: 600, fontSize: '1.125rem', lineHeight: 1.55 },
      subtitle2: { fontWeight: 600 },
      button: { fontWeight: 500, letterSpacing: 0 },
      overline: { fontWeight: 500, fontSize: '0.75rem', letterSpacing: '0.04em', lineHeight: 1.6 },
    },
    transitions: { easing: { easeInOut: ease } },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          ...cssVariables,
          html: { colorScheme: mode },
          body: { WebkitFontSmoothing: 'antialiased', MozOsxFontSmoothing: 'grayscale', transition: `background-color .3s ${ease}` },
          '::selection': { background: alpha(brand[500], 0.2) },
          '*::-webkit-scrollbar': { width: 6, height: 6 },
          '*::-webkit-scrollbar-track': { background: 'transparent' },
          '*::-webkit-scrollbar-thumb': { background: 'var(--ta-scroll-thumb)', borderRadius: 999 },
          '@keyframes ta-ping': { '75%, 100%': { transform: 'scale(2)', opacity: 0 } },
          '@keyframes ta-fade-up': { from: { opacity: 0, transform: 'translateY(8px)' }, to: { opacity: 1, transform: 'none' } },
          '@keyframes ta-spin': { to: { transform: 'rotate(360deg)' } },
          '@media (prefers-reduced-motion: reduce)': { '*, *::before, *::after': { animationDuration: '0.01ms !important', transitionDuration: '0.01ms !important' } },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { textTransform: 'none', fontWeight: 500, borderRadius: 8, paddingInline: 16, transition: `all .2s ${ease}` },
          sizeLarge: { padding: '12px 20px', fontSize: '0.875rem' },
          sizeMedium: { padding: '9px 16px' },
          sizeSmall: { padding: '5px 12px', fontSize: '0.8125rem' },
          containedPrimary: {
            backgroundColor: brand[500],
            boxShadow: shadows.xs,
            '&:hover': { backgroundColor: brand[600], boxShadow: shadows.xs },
            '&.Mui-disabled': dark ? { backgroundColor: 'rgba(70,95,255,0.35)', color: 'rgba(255,255,255,0.5)' } : { backgroundColor: brand[300], color: '#fff' },
          },
          outlined: {
            borderColor: c.inputLine,
            color: c.ink2,
            backgroundColor: dark ? gray[800] : '#fff',
            boxShadow: shadows.xs,
            '&:hover': { borderColor: c.inputLine, backgroundColor: dark ? 'rgba(255,255,255,0.03)' : gray[50], color: dark ? gray[300] : gray[800] },
          },
          text: { color: dark ? brand[400] : brand[500], '&:hover': { backgroundColor: dark ? 'rgba(70,95,255,0.12)' : brand[50] } },
        },
      },
      MuiIconButton: { styleOverrides: { root: { borderRadius: 8, transition: `all .2s ${ease}` } } },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: { backgroundImage: 'none' },
          outlined: { borderColor: c.line },
          elevation0: { border: `1px solid ${c.line}`, borderRadius: 16, backgroundColor: 'var(--ta-card)' },
        },
      },
      MuiCard: { styleOverrides: { root: { borderRadius: 16, borderColor: c.line, backgroundColor: 'var(--ta-card)' } } },
      MuiAppBar: { styleOverrides: { root: { border: 'none', boxShadow: 'none' } } },
      MuiDrawer: { styleOverrides: { paper: { border: 'none', boxShadow: 'none', backgroundColor: 'var(--ta-surface)' } } },
      MuiMenu: {
        styleOverrides: {
          paper: { border: `1px solid ${c.line}`, boxShadow: shadows.lg, borderRadius: 16, marginTop: 8, backgroundColor: c.paper },
          list: { padding: 12 },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 500,
            color: c.ink2,
            minHeight: 40,
            padding: '8px 12px',
            '&:hover': { backgroundColor: c.hover },
            '&.Mui-selected': { backgroundColor: dark ? 'rgba(70,95,255,0.12)' : brand[50], color: dark ? brand[400] : brand[500] },
            '&.Mui-selected:hover': { backgroundColor: dark ? 'rgba(70,95,255,0.16)' : brand[100] },
          },
        },
      },
      MuiPopover: { styleOverrides: { paper: { boxShadow: shadows.lg, borderRadius: 16, border: `1px solid ${c.line}` } } },
      MuiAutocomplete: {
        styleOverrides: {
          paper: { borderRadius: 12, border: `1px solid ${c.line}`, boxShadow: shadows.lg, marginTop: 4 },
          option: { fontSize: 14, borderRadius: 8, margin: '0 6px' },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: { borderRadius: 24, border: dark ? `1px solid ${gray[800]}` : 'none', backgroundColor: dark ? gray[900] : '#fff', boxShadow: shadows.xl },
        },
      },
      MuiBackdrop: { styleOverrides: { root: { '&:not(.MuiBackdrop-invisible)': { backgroundColor: 'rgba(152,162,179,0.5)', backdropFilter: 'blur(32px)' } } } },
      MuiDialogTitle: { styleOverrides: { root: { fontWeight: 600, fontSize: '1.25rem', padding: '24px 24px 12px' } } },
      MuiDialogContent: { styleOverrides: { root: { padding: '8px 24px' } } },
      MuiDialogActions: { styleOverrides: { root: { padding: '16px 24px 24px', gap: 4 } } },
      MuiTable: { styleOverrides: { root: { borderCollapse: 'separate', borderSpacing: 0 } } },
      MuiTableCell: {
        styleOverrides: {
          root: { borderBottom: `1px solid ${c.lineSoft}`, fontSize: 14, color: c.muted, padding: '14px 20px' },
          head: { fontWeight: 500, fontSize: 12, color: c.muted, background: 'transparent', whiteSpace: 'nowrap', paddingTop: 12, paddingBottom: 12 },
          sizeSmall: { padding: '10px 16px' },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            transition: `background-color .2s ${ease}`,
            '&.MuiTableRow-hover:hover': { backgroundColor: dark ? 'rgba(255,255,255,0.02)' : gray[50] },
            '&:last-of-type td': { borderBottom: 'none' },
          },
        },
      },
      MuiTablePagination: { styleOverrides: { root: { borderTop: `1px solid ${c.lineSoft}`, color: c.muted }, toolbar: { minHeight: 56 } } },
      MuiTextField: { defaultProps: { size: 'small' } },
      // Show the empty option's text (e.g. "Any") instead of a blank box, since labels sit above the field.
      MuiSelect: { defaultProps: { size: 'small', displayEmpty: true } },
      // TailAdmin forms: the label sits above the field rather than floating inside it.
      MuiFormControl: { styleOverrides: { root: { '& > .MuiInputLabel-root + .MuiInputBase-root': { marginTop: 0 } } } },
      MuiInputLabel: {
        defaultProps: { shrink: true },
        styleOverrides: {
          root: {
            position: 'relative',
            transform: 'none',
            fontSize: 14,
            fontWeight: 500,
            color: c.ink2,
            marginBottom: 6,
            lineHeight: '20px',
            overflow: 'visible',
            whiteSpace: 'nowrap',
            '&.Mui-focused': { color: c.ink2 },
            '&.Mui-error': { color: error[500] },
            '& .MuiFormLabel-asterisk': { color: error[500] },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontSize: 14,
            backgroundColor: dark ? gray[900] : 'transparent',
            boxShadow: shadows.xs,
            color: c.ink,
            transition: `box-shadow .2s ${ease}`,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: c.inputLine, top: 0, transition: `border-color .2s ${ease}` },
            '& .MuiOutlinedInput-notchedOutline legend': { display: 'none' },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: dark ? gray[600] : gray[400] },
            '&.Mui-focused': { boxShadow: `0 0 0 3px ${alpha(brand[500], dark ? 0.2 : 0.1)}` },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderWidth: 1, borderColor: dark ? brand[800] : brand[300] },
            '&.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: error[300] },
            '&.Mui-error.Mui-focused': { boxShadow: `0 0 0 3px ${alpha(error[500], 0.1)}` },
            '&.Mui-disabled': { backgroundColor: dark ? gray[800] : gray[50] },
          },
          input: {
            padding: '12px 16px',
            '&::placeholder': { color: dark ? 'rgba(255,255,255,0.3)' : gray[400], opacity: 1 },
          },
          inputSizeSmall: { padding: '10px 14px' },
        },
      },
      MuiFormHelperText: { styleOverrides: { root: { marginLeft: 0, marginTop: 6, fontSize: 12 } } },
      MuiChip: {
        styleOverrides: {
          root: { fontWeight: 500, borderRadius: 999 },
          sizeSmall: { height: 22, fontSize: 12 },
          labelSmall: { paddingLeft: 10, paddingRight: 10 },
          outlined: { borderColor: c.line, color: c.ink2 },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: { minHeight: 48 },
          indicator: { height: 2, borderRadius: 2, backgroundColor: brand[500] },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
            fontSize: 14,
            minHeight: 48,
            color: c.muted,
            transition: `color .2s ${ease}`,
            '&:hover': { color: c.ink2 },
            '&.Mui-selected': { color: dark ? brand[400] : brand[500] },
          },
        },
      },
      MuiTooltip: {
        defaultProps: { arrow: true },
        styleOverrides: {
          tooltip: { backgroundColor: dark ? gray[800] : gray[900], fontSize: 12, fontWeight: 500, padding: '6px 10px', borderRadius: 8 },
          arrow: { color: dark ? gray[800] : gray[900] },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: 12, alignItems: 'center', fontSize: 14 },
          standardError: { border: `1px solid ${error[500]}`, backgroundColor: dark ? 'rgba(240,68,56,0.15)' : error[50], color: dark ? error[500] : gray[800] },
          standardWarning: { border: `1px solid ${warning[500]}`, backgroundColor: dark ? 'rgba(247,144,9,0.15)' : warning[50], color: dark ? orange[400] : gray[800] },
          standardSuccess: { border: `1px solid ${success[500]}`, backgroundColor: dark ? 'rgba(18,183,106,0.15)' : success[50], color: dark ? success[500] : gray[800] },
          standardInfo: { border: `1px solid ${blueLight[500]}`, backgroundColor: dark ? 'rgba(11,165,236,0.15)' : blueLight[50], color: dark ? blueLight[500] : gray[800] },
        },
      },
      MuiLinearProgress: {
        styleOverrides: { root: { borderRadius: 999, height: 8, backgroundColor: dark ? gray[800] : gray[200] }, bar: { borderRadius: 999 } },
      },
      MuiAvatar: { styleOverrides: { root: { fontWeight: 500 } } },
      MuiBadge: { styleOverrides: { badge: { fontWeight: 600, fontSize: 10.5, minWidth: 18, height: 18 } } },
      MuiBreadcrumbs: { styleOverrides: { root: { fontSize: 14 }, separator: { color: c.muted } } },
      // TailAdmin toggle: 44x24 pill, white knob, brand track when on.
      MuiSwitch: {
        styleOverrides: {
          root: { width: 44, height: 24, padding: 0, margin: '0 10px 0 4px' },
          switchBase: {
            padding: 2,
            '&.Mui-checked': { transform: 'translateX(20px)', color: '#fff' },
            '&.Mui-checked + .MuiSwitch-track': { backgroundColor: brand[500], opacity: 1 },
          },
          thumb: { width: 20, height: 20, boxShadow: shadows.sm, backgroundColor: '#fff' },
          track: { borderRadius: 12, backgroundColor: dark ? 'rgba(255,255,255,0.1)' : gray[200], opacity: 1, transition: `background-color .3s ${ease}` },
        },
      },
      MuiCheckbox: { styleOverrides: { root: { color: c.inputLine, '&.Mui-checked': { color: brand[500] } } } },
      MuiRadio: { styleOverrides: { root: { color: c.inputLine, '&.Mui-checked': { color: brand[500] } } } },
      MuiDivider: { styleOverrides: { root: { borderColor: c.line } } },
      MuiListSubheader: { styleOverrides: { root: { lineHeight: '20px' } } },
      MuiSkeleton: { defaultProps: { animation: 'wave' }, styleOverrides: { root: { backgroundColor: dark ? gray[800] : gray[100] } } },
      // TailAdmin segmented control: grey track, the selected segment lifts onto a white pill.
      MuiToggleButtonGroup: {
        styleOverrides: {
          root: { backgroundColor: dark ? gray[900] : gray[100], borderRadius: 8, padding: 2, gap: 2, border: dark ? `1px solid ${gray[800]}` : 'none' },
          grouped: { border: 0, borderRadius: '6px !important', margin: 0 },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
            fontSize: 14,
            color: c.muted,
            padding: '7px 14px',
            transition: `all .2s ${ease}`,
            '&:hover': { backgroundColor: 'transparent', color: c.ink },
            '&.Mui-selected, &.Mui-selected:hover': { backgroundColor: dark ? gray[800] : '#fff', color: c.ink, boxShadow: shadows.xs },
          },
        },
      },
    },
  });
}
